/* eslint-disable react/prop-types, no-console */
import React, { useState, useMemo, useCallback } from 'react';
import { Drawer, Form, Input, Row, Col, Divider, Button, Space, Badge, message } from 'antd';
import { CheckOutlined, CloseOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import CustomAvatar from '../Global/CustomAvatar/CustomAvatar';
import ImageComponent from '../Global/ImageComponent/ImageComponent';
import DocumentPreviewModal from './DocumentPreviewModal';

// Memoized components to prevent unnecessary re-renders
const MemoizedInput = React.memo(Input);
const MemoizedFormItem = React.memo(Form.Item);
const MemoizedButton = React.memo(Button);

const DriverDetailsDrawer = ({ open, onClose, driver, onApprove, onDeny, onViewDocument }) => {
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewDocuments, setPreviewDocuments] = useState([]);

	// Memoize document filtering to prevent recalculation on every render
	const documentsByType = useMemo(() => {
		if (!driver?.driverDocuments || !Array.isArray(driver.driverDocuments)) {
			return {
				ID_CARD: [],
				DRIVER_LICENSE_DOCUMENT: [],
				TAG_DOCUMENT: [],
				POLICE_CONDUCT_DOCUMENT: [],
			};
		}

		return {
			ID_CARD: driver.driverDocuments.filter(
				(doc) => doc?.type === 'ID_CARD' || doc?.documentType === 'ID_CARD',
			),
			DRIVER_LICENSE_DOCUMENT: driver.driverDocuments.filter(
				(doc) =>
					doc?.type === 'DRIVER_LICENSE_DOCUMENT' ||
					doc?.documentType === 'DRIVER_LICENSE_DOCUMENT',
			),
			TAG_DOCUMENT: driver.driverDocuments.filter(
				(doc) => doc?.type === 'TAG_DOCUMENT' || doc?.documentType === 'TAG_DOCUMENT',
			),
			POLICE_CONDUCT_DOCUMENT: driver.driverDocuments.filter(
				(doc) =>
					doc?.type === 'POLICE_CONDUCT_DOCUMENT' ||
					doc?.documentType === 'POLICE_CONDUCT_DOCUMENT',
			),
		};
	}, [driver?.driverDocuments]);

	// Memoize computed values
	const driverDisplayName = useMemo(() => {
		if (!driver) return '';
		return `${driver.name} ${driver.surname}`;
	}, [driver]);

	const formattedPhone = useMemo(() => {
		if (!driver) return 'N/A';
		return driver.phoneCountryCode
			? `+${driver.phoneCountryCode} ${driver.phone}`
			: driver.phone || 'N/A';
	}, [driver]);

	const formattedRegistrationDate = useMemo(() => {
		if (!driver?.applicationSubmittedAt) return 'N/A';
		return dayjs(driver.applicationSubmittedAt).format('DD/MM/YYYY');
	}, [driver?.applicationSubmittedAt]);

	const formattedLicenseExpiry = useMemo(() => {
		if (!driver?.drivingLicenceExpiryDate) return null;
		return dayjs(driver.drivingLicenceExpiryDate).format('YYYY/MM/DD');
	}, [driver?.drivingLicenceExpiryDate]);

	const handleApprove = useCallback(() => {
		if (onClose) onClose();
		if (onApprove && driver) onApprove(driver);
	}, [onClose, onApprove, driver]);

	const handleDeny = useCallback(() => {
		if (onClose) onClose();
		if (onDeny && driver) onDeny(driver);
	}, [onClose, onDeny, driver]);

	const handlePreviewDocuments = useCallback((documents) => {
		if (documents.length > 0) {
			setPreviewDocuments(documents);
			setPreviewOpen(true);
		} else {
			message.info('No documents available');
		}
	}, []);

	// Memoize fleet operators rendering to prevent re-renders
	const fleetOperatorsSection = useMemo(() => {
		if (!driver?.fleetOperators || !Array.isArray(driver.fleetOperators)) {
			return (
				<Row gutter={16}>
					<Col span={24}>
						<MemoizedFormItem>
							<MemoizedInput value='No fleet operators assigned' disabled />
						</MemoizedFormItem>
					</Col>
				</Row>
			);
		}

		if (driver.fleetOperators.length === 0) {
			return (
				<Row gutter={16}>
					<Col span={24}>
						<MemoizedFormItem>
							<MemoizedInput value='No fleet operators assigned' disabled />
						</MemoizedFormItem>
					</Col>
				</Row>
			);
		}

		return driver.fleetOperators.map((operator, index) => (
			<Row gutter={16} key={operator.id || index} style={{ marginBottom: 16 }}>
				<Col span={12}>
					<MemoizedFormItem label={index === 0 ? 'Operator Name' : ''}>
						<MemoizedInput value={operator.name || 'N/A'} disabled />
					</MemoizedFormItem>
				</Col>
				<Col span={12}>
					<MemoizedFormItem label={index === 0 ? 'Operator Contact' : ''}>
						<MemoizedInput value={operator.contactPhone || 'N/A'} disabled />
					</MemoizedFormItem>
				</Col>
			</Row>
		));
	}, [driver?.fleetOperators]);

	// Memoize document sections to prevent unnecessary re-renders
	const documentSections = useMemo(() => {
		const today = dayjs();
		const sections = [
			{
				key: 'ID_CARD',
				label: 'ID Card Documents',
				documents: documentsByType.ID_CARD,
			},
			{
				key: 'DRIVER_LICENSE_DOCUMENT',
				label: 'Driving License Documents',
				documents: documentsByType.DRIVER_LICENSE_DOCUMENT,
			},
			{
				key: 'TAG_DOCUMENT',
				label: 'Tag Documents',
				documents: documentsByType.TAG_DOCUMENT,
			},
			{
				key: 'POLICE_CONDUCT_DOCUMENT',
				label: 'Police Conduct Documents',
				documents: documentsByType.POLICE_CONDUCT_DOCUMENT,
			},
		];

		return sections.map((section) => {
			const firstDoc = section.documents?.[0];
			const expiryDate = firstDoc?.expiryDate;
			const formattedExpiry = expiryDate ? dayjs(expiryDate).format('DD/MM/YYYY') : null;
			const isExpired = expiryDate ? dayjs(expiryDate).isBefore(today, 'day') : false;

			return (
				<Row key={section.key} gutter={16} style={{ alignItems: 'flex-end', marginBottom: 16 }}>
					<Col span={12}>
						<MemoizedFormItem label={section.label} style={{ marginBottom: 0 }}>
							<MemoizedInput
								value={`${section.documents.length} file(s) uploaded`}
								disabled
							/>
						</MemoizedFormItem>
					</Col>
					<Col span={6}>
						<MemoizedFormItem label="Expiry Date" style={{ marginBottom: 0 }}>
							<MemoizedInput
								value={formattedExpiry || 'N/A'}
								disabled
								style={{
									color: isExpired ? '#ff4d4f' : undefined,
								}}
							/>
						</MemoizedFormItem>
					</Col>
					<Col span={6}>
						<MemoizedFormItem style={{ marginBottom: 0 }}>
							<MemoizedButton
								icon={<FileTextOutlined />}
								onClick={() => handlePreviewDocuments(section.documents)}
								disabled={section.documents.length === 0}>
								View Files ({section.documents.length})
							</MemoizedButton>
						</MemoizedFormItem>
					</Col>
				</Row>
			);
		});
	}, [documentsByType, handlePreviewDocuments]);

	// Early return if no driver data
	if (!driver) {
		return null;
	}

	// Only render when drawer is open to improve performance
	if (!open) {
		return (
			<Drawer title='Driver Application Details' width={800} onClose={onClose} open={false} />
		);
	}

	return (
		<Drawer
			title='Driver Application Details'
			width={800}
			onClose={onClose}
			open={open}
			footer={
				<div style={{ textAlign: 'right' }}>
					<Space>
						<Button onClick={onClose}>Close</Button>
						<Button
							type='primary'
							icon={<CheckOutlined />}
							onClick={handleApprove}
							style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>
							Approve
						</Button>
						<Button type='primary' danger icon={<CloseOutlined />} onClick={handleDeny}>
							Deny
						</Button>
					</Space>
				</div>
			}>
			{driver && (
				<Form layout='vertical' disabled>
					{/* Profile Picture */}
					<MemoizedFormItem label='Profile Picture'>
						<div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
							{driver.profilePicture ? (
								<ImageComponent size={80} imageId={driver.profilePicture} />
							) : (
								<CustomAvatar name={driverDisplayName} type='initials' size={80} />
							)}
							<div>
								<h3 style={{ margin: 0 }}>{driverDisplayName}</h3>
								<Badge status='processing' text='PENDING APPROVAL' />
							</div>
						</div>
					</MemoizedFormItem>

					<Divider>Personal Information</Divider>

					<Row gutter={16}>
						<Col span={12}>
							<MemoizedFormItem label='First Name'>
								<MemoizedInput value={driver.name} disabled />
							</MemoizedFormItem>
						</Col>
						<Col span={12}>
							<MemoizedFormItem label='Last Name'>
								<MemoizedInput value={driver.surname} disabled />
							</MemoizedFormItem>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col span={12}>
							<MemoizedFormItem label='Email'>
								<MemoizedInput value={driver.email} disabled />
							</MemoizedFormItem>
						</Col>
						<Col span={12}>
							<MemoizedFormItem label='Phone'>
								<MemoizedInput value={formattedPhone} disabled />
							</MemoizedFormItem>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col span={12}>
							<MemoizedFormItem label='Gender'>
								<MemoizedInput value={driver.gender || 'N/A'} disabled />
							</MemoizedFormItem>
						</Col>
						<Col span={12}>
							<MemoizedFormItem label='Registration Date'>
								<MemoizedInput value={formattedRegistrationDate} disabled />
							</MemoizedFormItem>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col span={6}>
							<MemoizedFormItem label='Country'>
								<MemoizedInput value={driver.country || 'N/A'} disabled />
							</MemoizedFormItem>
						</Col>
						<Col span={18}>
							<MemoizedFormItem label='Address'>
								<MemoizedInput value={driver.address || 'N/A'} disabled />
							</MemoizedFormItem>
						</Col>
					</Row>

					{/* License Information */}
					{(driver.drivingLicenceNumber || driver.drivingLicenceExpiryDate) && (
						<>
							<Divider>License Information</Divider>
							<Row gutter={16}>
								{driver.drivingLicenceNumber && (
									<Col span={12}>
										<MemoizedFormItem label='License Number'>
											<MemoizedInput
												value={driver.drivingLicenceNumber}
												disabled
											/>
										</MemoizedFormItem>
									</Col>
								)}
								{driver.drivingLicenceExpiryDate && (
									<Col span={12}>
										<MemoizedFormItem label='License Expiry'>
											<MemoizedInput
												value={formattedLicenseExpiry}
												disabled
											/>
										</MemoizedFormItem>
									</Col>
								)}
							</Row>
						</>
					)}
					{/* Fleet Operator Information */}
					<Divider>Fleet Operators</Divider>
					{fleetOperatorsSection}

					{/* Documents Section */}
					<Divider>Documents</Divider>
					{documentSections}

					{/* Tenant Information */}
					{driver.tenant && (
						<>
							<Divider>Tenant Information</Divider>
							<MemoizedFormItem label='Tenant'>
								<MemoizedInput value={driver.tenant.name} disabled />
							</MemoizedFormItem>
						</>
					)}
				</Form>
			)}

			{/* Document Preview Modal */}
			<DocumentPreviewModal
				open={previewOpen}
				onClose={() => setPreviewOpen(false)}
				documents={previewDocuments}
				onViewDocument={onViewDocument}
			/>
		</Drawer>
	);
};

export default DriverDetailsDrawer;
/* eslint-enable react/prop-types, no-console */
